import { ChangeDetectorRef, Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import {
  CIPXQuoteRequestIntakeForm,
  CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig,
  CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup,
  CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods,
  CIPXQuoteRequestIntakeFormCommunicationsContact,
  CIPXQuoteRequestIntakeFormCommunicationsContactType,
  CIPXQuoteRequestIntakeFormCommunicationsContactTypeType,
  CIPXQuoteRequestIntakeFormCommunicationsEmailMethod,
  CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod,
  CIPXQuoteRequestIntakeFormCommunicationsSmsMethod,
} from '../../intake-form.types';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@sbd-doug-frontend/shared/lib/shared-components/shared-components.module';
import { CIPXQuoteRequestIntakeFormService } from '../../intake-form.service';
import { CURRENT_USER_TOKEN, SharedUserService } from '@sbd-doug-frontend/shared/lib/types/current-user';
import { CIPXPrimaryButtonComponent } from '@sbd-doug-frontend/shared/lib/ui';
import { MatDialog } from '@angular/material/dialog';
import { IntakeFormContactsDialogComponent } from './intake-form-contacts-dialog.component';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { take } from 'rxjs';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    SharedComponentsModule,
    CIPXPrimaryButtonComponent,
    NgxMaskDirective,
  ],
  providers: [provideNgxMask()],
  selector: 'cipx-quote-request-intake-form-notifications',
  template: `
    <div class="cipx-form-section">
      <fieldset>
        <legend class="cipx-form-section-title">Notification Preferences</legend>

        <div class="cipx-form-form-group">
          <p>
            Based on your settings, by default only you will be notified when the quote is ready. You can change the
            notifications for this quote request below.
          </p>
          <p>
            If you add email addresses for the owners or other contacts at
            {{ quoteRequestForm.controls.requestDetails.controls.principal.controls.company_name.value }}, they will not
            be notified, but it may save time if their signatures are required
          </p>
        </div>
      </fieldset>
    </div>

    <div class="cipx-form-section">
      <fieldset>
        <div class="cipx-form-form-group">
          <div class="mb-2 font-medium">Email</div>
          <!-- Me Row (Account User) -->
          @if (accountUser) {
            <div class="flex items-center gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(accountUser, 'communication_config.communication_group.quote_announcements.email.enabled')">
                Me: {{ contactControl(accountUser, 'email').value || '—' }}
              </mat-checkbox>
            </div>
          }
          @for (contact of userContacts; track contact) {
            <div class="flex flex-col items-start gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(contact, 'communication_config.communication_group.quote_announcements.email.enabled')">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </mat-checkbox>
              <mat-form-field appearance="fill" class="w-80 ml-11">
                <input
                  matInput
                  placeholder="Email"
                  [value]="contactControl(contact, 'email').value"
                  [disabled]="!contactControl(contact, 'communication_config.communication_group.quote_announcements.email.enabled').value"
                  (input)="setContactTextValue(contact, 'email', $event)" />
                <mat-error *ngIf="contactControl(contact, 'email').touched && contactControl(contact, 'email').invalid">
                  <ng-container *ngIf="contactControl(contact, 'email').hasError('required'); else emailInvalid">
                    Email is required.
                  </ng-container>
                  <ng-template #emailInvalid>
                    Please enter a valid email address.
                  </ng-template>
                </mat-error>
              </mat-form-field>
            </div>
          }

        </div>
      </fieldset>
      <fieldset>
        <div class="cipx-form-form-group">
          <div class="mb-2 font-medium">Phone</div>
          <!-- Me Row (Account User) -->
          @if (accountUser) {
            <div class="flex items-center gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(accountUser, 'communication_config.communication_group.quote_announcements.phone.enabled')">
                Me: {{ contactControl(accountUser, 'phone').value || '—' }}
              </mat-checkbox>
            </div>
          }
          @for (contact of userContacts; track contact) {
            <div class="flex flex-col items-start gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(contact, 'communication_config.communication_group.quote_announcements.phone.enabled')">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </mat-checkbox>
              <mat-form-field appearance="fill" class="w-80 ml-11">
                <input
                  matInput
                  [formControl]="contactControl(contact, 'phone')"
                  placeholder="Phone"
                  mask="(000) 000-0000"
                  [dropSpecialCharacters]="false" />
              </mat-form-field>
            </div>
          }
        </div>
      </fieldset>
      <fieldset>
        <div class="cipx-form-form-group">
          <div class="flex flex-row items-center gap-3 justify-between w-full">
            <h3 class="font-medium">Additional Contacts</h3>
            <cipx-primary-button
              variant="stroked"
              [color]="'default'"
              class="ml-auto"
              icon="heroicons_outline:book-open"
              (buttonClick)="addListenerModal()">
              Contacts
            </cipx-primary-button>
          </div>
          @if (listenerContacts.length === 0) {
            <div class="pl-4 text-sm text-gray-500 italic">No additional contacts will be added.</div>
          }
          @for (contact of listenerContacts; track contact) {
            <div class="pl-4">
              <div class="font-medium">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </div>
              <div class="flex flex-col gap-2 mt-1">
                <mat-checkbox
                  [formControl]="contactControl(contact, 'communication_config.communication_group.quote_announcements.email.enabled')"
                  [disabled]="!hasEmailValue(contact)">
                  Email: {{ truncateWithEllipsis(contactControl(contact, 'email').value) }}
                </mat-checkbox>
                <mat-checkbox
                  [formControl]="contactControl(contact, 'communication_config.communication_group.quote_announcements.phone.enabled')"
                  [disabled]="!hasPhoneValue(contact)">
                  Phone: {{ formatPhoneDisplay(contactControl(contact, 'phone').value) }}
                </mat-checkbox>
              </div>
            </div>
          }
        </div>
      </fieldset>
      <!-- SMS selections hidden for now -->
    </div>
    <div class="cipx-form-section">
      <fieldset>
        <legend class="cipx-form-section-title">Next Renewal Preferences</legend>
        <div class="cipx-form-form-group">
          <div class="mb-2 font-medium">Email</div>
          @if (accountUser) {
            <div class="flex items-center gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(accountUser, 'communication_config.communication_group.quote_next_renewal.email.enabled')">
                Me: {{ contactControl(accountUser, 'email').value || '—' }}
              </mat-checkbox>
            </div>
          }
          @for (contact of userContacts; track contact) {
            <div class="flex flex-col items-start gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(contact, 'communication_config.communication_group.quote_next_renewal.email.enabled')">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </mat-checkbox>
              <mat-form-field appearance="fill" class="w-80 ml-11">
                <input
                  matInput
                  placeholder="Email"
                  [value]="contactControl(contact, 'email').value"
                  [disabled]="!contactControl(contact, 'communication_config.communication_group.quote_next_renewal.email.enabled').value"
                  (input)="setContactTextValue(contact, 'email', $event)" />
              </mat-form-field>
            </div>
          }

        </div>
        <div class="cipx-form-form-group mt-4">
          <div class="mb-2 font-medium">Phone</div>
          @if (accountUser) {
            <div class="flex items-center gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(accountUser, 'communication_config.communication_group.quote_next_renewal.phone.enabled')">
                Me: {{ formatPhoneDisplay(contactControl(accountUser, 'phone').value) }}
              </mat-checkbox>
            </div>
          }
          @for (contact of userContacts; track contact) {
            <div class="flex flex-col items-start gap-3 pl-4">
              <mat-checkbox [formControl]="contactControl(contact, 'communication_config.communication_group.quote_next_renewal.phone.enabled')">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </mat-checkbox>
              <mat-form-field appearance="fill" class="w-80 ml-11">
                <input
                  matInput
                  placeholder="Phone"
                  inputmode="tel"
                  [value]="formatPhoneInputValue(contactControl(contact, 'phone').value)"
                  [disabled]="!contactControl(contact, 'communication_config.communication_group.quote_next_renewal.phone.enabled').value"
                  (input)="setContactTextValue(contact, 'phone', $event)" />
              </mat-form-field>
            </div>
          }
        </div>
        <div class="cipx-form-form-group mt-4">
          <div class="flex flex-row items-center gap-3 justify-between w-full">
            <h3 class="font-medium">Additional Contacts</h3>
            <cipx-primary-button
              variant="stroked"
              [color]="'default'"
              class="ml-auto"
              icon="heroicons_outline:book-open"
              (buttonClick)="addListenerModal()">
              Contacts
            </cipx-primary-button>
          </div>
          @if (listenerContacts.length === 0) {
            <div class="pl-4 text-sm text-gray-500 italic">No additional contacts will be added.</div>
          }
          @for (contact of listenerContacts; track contact) {
            <div class="pl-4">
              <div class="font-medium">
                {{ contact.get('first_name')?.value }} {{ contact.get('last_name')?.value }}
              </div>
              <div class="flex flex-col gap-2 mt-1">
                <mat-checkbox
                  [formControl]="contactControl(contact, 'communication_config.communication_group.quote_next_renewal.email.enabled')"
                  [disabled]="!hasEmailValue(contact)">
                  Email: {{ truncateWithEllipsis(contactControl(contact, 'email').value) }}
                </mat-checkbox>
                <mat-checkbox
                  [formControl]="contactControl(contact, 'communication_config.communication_group.quote_next_renewal.phone.enabled')"
                  [disabled]="!hasPhoneValue(contact)">
                  Phone: {{ formatPhoneDisplay(contactControl(contact, 'phone').value) }}
                </mat-checkbox>
              </div>
            </div>
          }
        </div>
        <!-- SMS selections hidden for now -->
      </fieldset>
    </div>
    <!-- <div class="cipx-form-section">
      <div>{{ _storedCommunicationData | json }}</div>
    </div> -->
  `,
})
export class CIPXQuoteRequestIntakeFormNotificationsComponent implements OnInit {
  private _intakeService = inject(CIPXQuoteRequestIntakeFormService);
  private _currentUser = inject<SharedUserService | null>(CURRENT_USER_TOKEN, { optional: true });
  private _changeDetectorRef = inject(ChangeDetectorRef);
  public _storedCommunicationData: { communicationInfo: any | null; principalContacts: any[] | null } | null = null;
  @Input() quoteRequestForm!: FormGroup<CIPXQuoteRequestIntakeForm>;
  private _dialog = inject(MatDialog);

  private _flagEnabledDefaultTrue(value: unknown): boolean {
    // API sometimes returns "" for false; treat undefined/null as "missing" => default true.
    if (value === undefined || value === null) return true;
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private _flagEnabledDefaultFalse(value: unknown): boolean {
    // Treat missing/empty as false unless explicitly truthy.
    return value === true || value === 'true' || value === 1 || value === '1';
  }

  private _applyStoredCommunicationToContacts(): void {
    const stored = this._intakeService.getStoredCommunicationData();
    this._storedCommunicationData = stored;
    const info = stored.communicationInfo as any | null;
    const contacts: any[] = Array.isArray(info?.contacts) ? info.contacts : [];
    if (contacts.length === 0) {
      return;
    }

    const findByUserId = (id: number): FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact> | null => {
      for (const c of this.contactsArray.controls) {
        const cid = Number((c.get('user_id')?.value as any) ?? NaN);
        if (!Number.isNaN(cid) && cid === id) {
          return c as FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>;
        }
      }
      return null;
    };

    const groups = ['quote_announcements', 'quote_next_renewal'] as const;
    const methods = ['email', 'phone', 'sms'] as const;

    contacts.forEach((src) => {
      const userId = Number((src as any)?.user_id ?? NaN);
      if (Number.isNaN(userId)) return;

      const target = findByUserId(userId);
      if (!target) return;

      for (const groupKey of groups) {
        for (const method of methods) {
          const enabled = Boolean(
            (src as any)?.communication_config?.communication_group?.[groupKey]?.[method]?.enabled
          );
          const ctrl = target.get(
            `communication_config.communication_group.${groupKey}.${method}.enabled`
          ) as FormControl | null;
          ctrl?.setValue(enabled, { emitEvent: false });
        }
      }
    });
    this._syncListenerContactAvailability();
  }

  contactControlGroup(group: FormGroup, path: string): FormGroup {
    // console.log('🔍 contactControlGroup', group, path);
    return group.get(path) as FormGroup;
  }

  contactControl(group: FormGroup, path: string): FormControl {
    // console.log('🔍 contactControl', group, path);
    return group.get(path) as FormControl;
  }

  public setContactTextValue(group: FormGroup, field: 'email' | 'phone', event: Event): void {
    const target = event.target as HTMLInputElement | null;
    const nextValue = target?.value ?? '';

    // When using proxy inputs (not bound via [formControl]), ngx-mask won't format.
    // Store the masked value in the FormControl so submissions keep formatting.
    if (field === 'phone') {
      const digits = nextValue.replace(/\D/g, '').slice(0, 10);
      const masked = this.formatPhoneInputValue(digits);
      const ctrl = this.contactControl(group, field);
      ctrl.setValue(masked);
      ctrl.markAsDirty();
      ctrl.markAsTouched();
      return;
    }
    const ctrl = this.contactControl(group, field);
    ctrl.setValue(nextValue);
    ctrl.markAsDirty();
    ctrl.markAsTouched();
  }

  // Helpers for communications.contacts access
  get contactsArray(): FormArray<FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>> {
    return this.quoteRequestForm.controls.communications.controls.contacts as unknown as FormArray;
  }

  public truncateWithEllipsis(value: unknown, max = 50): string {
    const str = typeof value === 'string' ? value : '';
    if (!str) return '—';
    return str.length > max ? str.slice(0, max) + '…' : str;
  }

  public hasEmailValue(contact: FormGroup): boolean {
    const value = String(this.contactControl(contact, 'email').value ?? '').trim();
    return value.length > 0;
  }

  public hasPhoneValue(contact: FormGroup): boolean {
    const raw = String(this.contactControl(contact, 'phone').value ?? '');
    const digits = raw.replace(/\D/g, '');
    return digits.length > 0;
  }

  private _syncListenerContactAvailability(): void {
    const listenerGroups = this.listenerContacts || [];
    const groups = ['quote_announcements', 'quote_next_renewal'] as const;
    listenerGroups.forEach((contact) => {
      for (const groupKey of groups) {
        const emailCtrl = this.contactControl(
          contact,
          `communication_config.communication_group.${groupKey}.email.enabled`
        );
        if (!this.hasEmailValue(contact)) {
          emailCtrl.setValue(false, { emitEvent: false });
          emailCtrl.disable({ emitEvent: false });
        } else {
          emailCtrl.enable({ emitEvent: false });
        }

        const phoneCtrl = this.contactControl(
          contact,
          `communication_config.communication_group.${groupKey}.phone.enabled`
        );
        if (!this.hasPhoneValue(contact)) {
          phoneCtrl.setValue(false, { emitEvent: false });
          phoneCtrl.disable({ emitEvent: false });
        } else {
          phoneCtrl.enable({ emitEvent: false });
        }
      }
    });
  }

  private _normalizeText(value: unknown): string {
    return String(value ?? '')
      .trim()
      .toLowerCase();
  }

  private _isOwnerContact(
    contact: { user_email_address?: string; user_first_name?: string; user_last_name?: string; user_phone?: string },
    ownersArray: FormArray
  ): boolean {
    const contactEmail = this._normalizeText(contact.user_email_address);
    const contactFirst = this._normalizeText(contact.user_first_name);
    const contactLast = this._normalizeText(contact.user_last_name);
    const contactPhone = String(contact.user_phone ?? '').replace(/\D/g, '');

    return ownersArray.controls.some((ownerCtrl) => {
      const ownerEmail = this._normalizeText(ownerCtrl.get('owner_email')?.value);
      if (contactEmail && ownerEmail && contactEmail === ownerEmail) {
        return true;
      }
      const ownerFirst = this._normalizeText(ownerCtrl.get('owner_first_name')?.value);
      const ownerLast = this._normalizeText(ownerCtrl.get('owner_last_name')?.value);
      const ownerPhone = String(ownerCtrl.get('owner_phone')?.value ?? '').replace(/\D/g, '');
      const nameMatches = contactFirst && contactLast && contactFirst === ownerFirst && contactLast === ownerLast;
      if (!nameMatches) return false;
      const phoneMatches = contactPhone && ownerPhone && contactPhone === ownerPhone;
      // Soft match on name when phones are missing or match.
      return phoneMatches || !contactPhone || !ownerPhone;
    });
  }

  get accountUser(): FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact> | null {
    const matches = this.contactsArray.controls.filter(
      (c: FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>) =>
        c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser
    );
    return (matches[0] as FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>) || null;
  }

  get userContacts(): FormGroup[] {
    return this.contactsArray.controls.filter(
      (c: FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>) =>
        c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.User
    ) as FormGroup[];
  }

  get listenerContacts(): FormGroup[] {
    return this.contactsArray.controls.filter(
      (c: FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>) =>
        c.get('contact_type')?.value === CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener
    ) as FormGroup[];
  }

  public listenerContactsForSection(section: 'quote_announcements' | 'quote_next_renewal'): FormGroup[] {
    const basePath = `communication_config.communication_group.${section}`;
    return (this.listenerContacts || []).filter((c) => {
      const emailEnabled = Boolean(c.get(`${basePath}.email.enabled`)?.value);
      const phoneEnabled = Boolean(c.get(`${basePath}.phone.enabled`)?.value);
      const smsEnabled = Boolean(c.get(`${basePath}.sms.enabled`)?.value);
      return emailEnabled || phoneEnabled || smsEnabled;
    });
  }

  addListener(): void {
    const group = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
      contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(
        CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener,
        { nonNullable: true }
      ),
      first_name: new FormControl(''),
      last_name: new FormControl(''),
      user_id: new FormControl<number | null>(null),
      email: new FormControl(''),
      phone: new FormControl(''),
      sms_phone: new FormControl(''),
      communication_config: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig>({
        communication_group: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup>({
          quote_announcements: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
            email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: new FormControl(false) }),
            phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: new FormControl(false) }),
            sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: new FormControl(false) }),
          }),
          quote_next_renewal: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
            email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: new FormControl(false) }),
            phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: new FormControl(false) }),
            sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: new FormControl({ value: false, disabled: true }) }),
          }),
        }),
      }),
    });
    this.contactsArray.push(group);
  }

  removeListener(index: number): void {
    const listeners = this.listenerContacts;
    if (index < 0 || index >= listeners.length) return;
    const ctrlToRemove = listeners[index];
    const absoluteIndex = this.contactsArray.controls.indexOf(ctrlToRemove);
    if (absoluteIndex >= 0) {
      this.contactsArray.removeAt(absoluteIndex);
    }
  }

  // Build owner/listener contacts lazily when notifications are displayed
  // This should be called by parent when navigating to the Notifications step or can be triggered here
  public ensureOwnerAndListenerContacts(): void {
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    // console.log('🔍 ensureOwnerAndListenerContacts', ownersArray);
    const stored = this._intakeService.getStoredCommunicationData();
    const principalContacts: any[] = Array.isArray(stored.principalContacts) ? stored.principalContacts : [];
    // Compute customer defaults from current user's preferences
    const cu = this._currentUser?.current?.();
    const userInfo: any | null = (cu as any)?.user_info ?? null;
    const allPrefs: any | null = userInfo?.preferences ?? null;
    const notifications: any | null = allPrefs?.notifications ?? null;
    const notifyMeBy: any | null = notifications?.notify_me_by ?? null;
    const renewalFlags: any | null = notifications?.qr_renewal_compliance_notifications ?? null;
    const emailEnabledDefault = Boolean(notifyMeBy?.notify_me_email);
    const phoneEnabledDefault = Boolean(notifyMeBy?.notify_me_phone);
    const enableAnnouncementsForCustomer = this._flagEnabledDefaultFalse(renewalFlags?.quotecustomer);
    const enableRenewalForCustomer = this._flagEnabledDefaultFalse(renewalFlags?.renewalcustomer);

    // Remove any existing derived contacts (keep account user + manually added listeners with user_id)
    for (let i = this.contactsArray.length - 1; i >= 0; i--) {
      const ctrl = this.contactsArray.at(i) as FormGroup;
      const type = ctrl.get('contact_type')?.value ?? '';
      const userId = (ctrl.get('user_id')?.value as number | null) ?? null;
      const isManualListener =
        type === CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener && userId !== null;
      if (
        type !== CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser &&
        !isManualListener
      ) {
        this.contactsArray.removeAt(i);
      }
    }

    // Prefer principal_contacts when available (it includes user_id and matches saved communication selections)
    const accountUserId =
      Number(this.accountUser?.get('user_id')?.value ?? (cu as any)?.principal_user_id ?? (cu as any)?.id ?? NaN) ||
      null;
    if (principalContacts.length > 0) {
      principalContacts.forEach((pc) => {
        const userId = Number((pc as any)?.user_id ?? NaN);
        if (Number.isNaN(userId)) return;
        if (accountUserId !== null && userId === accountUserId) return;

        const emailAddress = String((pc as any)?.user_email_address ?? '');
        const phoneRaw = String((pc as any)?.user_phone ?? '');
        const phoneDigits = phoneRaw.replace(/\D/g, '');
        const phoneHasValue = phoneDigits.length >= 10;

        const emailEnabled = new FormControl(enableAnnouncementsForCustomer && emailEnabledDefault);
        const phoneEnabled = new FormControl(enableAnnouncementsForCustomer && phoneEnabledDefault && phoneHasValue);
        const renewalEmailEnabled = new FormControl(enableRenewalForCustomer && emailEnabledDefault);
        const renewalPhoneEnabled = new FormControl(enableRenewalForCustomer && phoneEnabledDefault && phoneHasValue);
        const smsEnabled = new FormControl(false);

        const emailField = new FormControl(emailAddress, [Validators.email]);
        const phoneField = new FormControl(phoneRaw);
        const smsField = new FormControl('');

        const isOwner = this._isOwnerContact(pc as any, ownersArray);
        const contactType = isOwner
          ? CIPXQuoteRequestIntakeFormCommunicationsContactType.User
          : CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener;
        const contact = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
          contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(contactType, {
            nonNullable: true,
          }),
          first_name: new FormControl(String((pc as any)?.user_first_name ?? '') || ''),
          last_name: new FormControl(String((pc as any)?.user_last_name ?? '') || ''),
          user_id: new FormControl<number | null>(userId),
          email: emailField,
          phone: phoneField,
          sms_phone: smsField,
          communication_config: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig>({
            communication_group: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup>({
              quote_announcements: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: emailEnabled }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: phoneEnabled }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: smsEnabled }),
              }),
              quote_next_renewal: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: renewalEmailEnabled }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: renewalPhoneEnabled }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({
                  enabled: new FormControl({ value: false, disabled: true }),
                }),
              }),
            }),
          }),
        });

        const syncEmailField = () => {
          const enabledForAny = Boolean(emailEnabled.value) || Boolean(renewalEmailEnabled.value);
          if (enabledForAny) {
            emailField.setValidators([Validators.required, Validators.email]);
            emailField.enable();
          } else {
            emailField.clearValidators();
            emailField.disable();
          }
          emailField.updateValueAndValidity({ emitEvent: false });
        };
        const syncPhoneField = () => {
          const enabledForAny = Boolean(phoneEnabled.value) || Boolean(renewalPhoneEnabled.value);
          if (enabledForAny) phoneField.enable();
          else phoneField.disable();
        };
        syncEmailField();
        syncPhoneField();
        if (!smsEnabled.value) smsField.disable();
        emailEnabled.valueChanges.subscribe(() => syncEmailField());
        renewalEmailEnabled.valueChanges.subscribe(() => syncEmailField());
        phoneEnabled.valueChanges.subscribe(() => syncPhoneField());
        renewalPhoneEnabled.valueChanges.subscribe(() => syncPhoneField());
        smsEnabled.valueChanges.subscribe((enabled) => {
          if (enabled) smsField.enable();
          else smsField.disable();
        });

        this.contactsArray.push(contact);
      });
    } else {
      // Fallback: Create contacts from owners array (no user_id available here)
      for (let i = 0; i < ownersArray.length; i++) {
        const owner = ownersArray.at(i);
        const firstName = String(owner.get('owner_first_name')?.value ?? '');
        const lastName = String(owner.get('owner_last_name')?.value ?? '');
        const emailAddress = String(owner.get('owner_email')?.value ?? '');
        const phoneRaw = String(owner.get('owner_phone')?.value ?? '');
        const phoneDigits = phoneRaw.replace(/\D/g, '');
        const phoneHasValue = phoneDigits.length >= 10;

        const emailEnabled = new FormControl(enableAnnouncementsForCustomer && emailEnabledDefault);
        const phoneEnabled = new FormControl(enableAnnouncementsForCustomer && phoneEnabledDefault && phoneHasValue);
        const renewalEmailEnabled = new FormControl(enableRenewalForCustomer && emailEnabledDefault);
        const renewalPhoneEnabled = new FormControl(enableRenewalForCustomer && phoneEnabledDefault && phoneHasValue);
        const smsEnabled = new FormControl(false);

        const emailField = new FormControl(emailAddress, [Validators.email]);
        const phoneField = new FormControl(phoneRaw);
        const smsField = new FormControl('');

        const contact = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
          contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(
            i === 0
              ? CIPXQuoteRequestIntakeFormCommunicationsContactType.User
              : CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener,
            { nonNullable: true }
          ),
          first_name: new FormControl(firstName || ''),
          last_name: new FormControl(lastName || ''),
          user_id: new FormControl<number | null>(null),
          email: emailField,
          phone: phoneField,
          sms_phone: smsField,
          communication_config: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig>({
            communication_group: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup>({
              quote_announcements: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: emailEnabled }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: phoneEnabled }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: smsEnabled }),
              }),
              quote_next_renewal: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: renewalEmailEnabled }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: renewalPhoneEnabled }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({
                  enabled: new FormControl({ value: false, disabled: true }),
                }),
              }),
            }),
          }),
        });

        const syncEmailField = () => {
          const enabledForAny = Boolean(emailEnabled.value) || Boolean(renewalEmailEnabled.value);
          if (enabledForAny) {
            emailField.setValidators([Validators.required, Validators.email]);
            emailField.enable();
          } else {
            emailField.clearValidators();
            emailField.disable();
          }
          emailField.updateValueAndValidity({ emitEvent: false });
        };
        const syncPhoneField = () => {
          const enabledForAny = Boolean(phoneEnabled.value) || Boolean(renewalPhoneEnabled.value);
          if (enabledForAny) phoneField.enable();
          else phoneField.disable();
        };
        syncEmailField();
        syncPhoneField();
        if (!smsEnabled.value) smsField.disable();
        emailEnabled.valueChanges.subscribe(() => syncEmailField());
        renewalEmailEnabled.valueChanges.subscribe(() => syncEmailField());
        phoneEnabled.valueChanges.subscribe(() => syncPhoneField());
        renewalPhoneEnabled.valueChanges.subscribe(() => syncPhoneField());
        smsEnabled.valueChanges.subscribe((enabled) => {
          if (enabled) smsField.enable();
          else smsField.disable();
        });

        this.contactsArray.push(contact);
      }
    }
    this._changeDetectorRef.detectChanges();
    // After building contacts, apply any stored communication toggles from service
    this._applyStoredCommunicationToContacts();
    this._syncListenerContactAvailability();
    this._changeDetectorRef.detectChanges();
  }

  ngOnInit(): void {
    this.buildAccountUserCommunications();
    // Ensure owner/listener contacts are created when arriving at Notifications
    this.ensureOwnerAndListenerContacts();
    // In case contacts already exist (e.g., step revisited), also try to apply stored data now
    this._applyStoredCommunicationToContacts();
    this._syncListenerContactAvailability();

    // Rebuild when owners change later (e.g., user edits owners before reaching notifications)
    const ownersArray = this.quoteRequestForm.controls.requestDetails.controls.owners;
    ownersArray.valueChanges.subscribe(() => setTimeout(() => this.ensureOwnerAndListenerContacts(), 0));
  }

  // Init work is done in ngOnInit; avoid recreating controls in ngAfterViewInit.
  public formatPhoneDisplay(value: string | null | undefined): string {
    const raw = (value ?? '').toString();
    const digits = raw.replace(/\D/g, '');
    if (digits.length === 10) {
      const a = digits.slice(0, 3);
      const b = digits.slice(3, 6);
      const c = digits.slice(6, 10);
      return `(${a}) ${b}-${c}`;
    }
    return raw || '—';
  }

  public formatPhoneInputValue(value: string | null | undefined): string {
    const raw = (value ?? '').toString();
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) {
      const a = digits.slice(0, 3);
      const b = digits.slice(3);
      return `(${a}) ${b}`;
    }
    const a = digits.slice(0, 3);
    const b = digits.slice(3, 6);
    const c = digits.slice(6);
    return `(${a}) ${b}-${c}`;
  }

  public buildAccountUserCommunications(): void {
    if (!this._currentUser?.get) {
      return;
    }

    this._currentUser
      .get()
      .pipe(take(1))
      .subscribe((cu) => {
        // console.log('🔍 Building account user communications:', cu);
        const userInfo: any | null = (cu as any)?.user_info ?? null;
        const allPrefs: any | null = userInfo?.preferences ?? null;
        const notifications: any | null = allPrefs?.notifications ?? null;
        const notifyMeBy: any | null = notifications?.notify_me_by ?? null;
        const renewalFlags: any | null = notifications?.qr_renewal_compliance_notifications ?? null;

        const emailEnabledDefault = Boolean(notifyMeBy?.notify_me_email);
        const phoneEnabledDefault = Boolean(notifyMeBy?.notify_me_phone);
        const smsEnabledDefault = Boolean(notifyMeBy?.notify_me_sms);

        // "me" flags default to enabled when missing, but API may send "" for false.
        const enableAnnouncementsForMe = this._flagEnabledDefaultTrue(renewalFlags?.quoteme);
        const enableRenewalForMe = this._flagEnabledDefaultTrue(renewalFlags?.renewalme);

        const alternateEmail: any | null = notifications?.alternate_email ?? null;
        const alternateEmailEnabled = alternateEmail?.alternate_email_enabled === true;
        const alternateSelectedEmail = String(alternateEmail?.alternate_selected_email ?? '').trim();
        const emailDefault =
          alternateEmailEnabled && alternateSelectedEmail ? alternateSelectedEmail : String(cu?.email ?? '');
        const phoneDefault = String(cu?.phone ?? '');
        const smsPhoneDefault = String(cu?.phone ?? '');
        let principalContactId = null;
        if (cu?.principal_user_id) {
          principalContactId = Number(cu.principal_user_id);
        } else {
          principalContactId = null;
        }
        const existing = this.accountUser;
        if (existing) {
          (existing.get('first_name') as FormControl)?.setValue(cu?.firstName ?? '');
          (existing.get('last_name') as FormControl)?.setValue(cu?.lastName ?? '');
          (existing.get('user_id') as FormControl<number | null>)?.setValue(principalContactId);
          (existing.get('email') as FormControl)?.setValue(emailDefault);
          (existing.get('phone') as FormControl)?.setValue(phoneDefault);
          (existing.get('sms_phone') as FormControl)?.setValue(smsPhoneDefault);
          const ann = this.contactControlGroup(existing, 'communication_config.communication_group.quote_announcements');
          (ann.get('email')?.get('enabled') as FormControl)?.setValue(enableAnnouncementsForMe ? emailEnabledDefault : false);
          (ann.get('phone')?.get('enabled') as FormControl)?.setValue(enableAnnouncementsForMe ? phoneEnabledDefault : false);
          (ann.get('sms')?.get('enabled') as FormControl)?.setValue(enableAnnouncementsForMe ? smsEnabledDefault : false);
          const next = this.contactControlGroup(existing, 'communication_config.communication_group.quote_next_renewal');
          (next.get('email')?.get('enabled') as FormControl)?.setValue(enableRenewalForMe ? emailEnabledDefault : false);
          (next.get('phone')?.get('enabled') as FormControl)?.setValue(enableRenewalForMe ? phoneEnabledDefault : false);
          (next.get('sms')?.get('enabled') as FormControl)?.setValue(enableRenewalForMe ? smsEnabledDefault : false);
          this._changeDetectorRef.detectChanges();
          return;
        }

        const group = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
          contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(
            CIPXQuoteRequestIntakeFormCommunicationsContactType.AccountUser,
            { nonNullable: true }
          ),
          first_name: new FormControl(cu?.firstName ?? ''),
          last_name: new FormControl(cu?.lastName ?? ''),
          user_id: new FormControl<number | null>(principalContactId),
          email: new FormControl(emailDefault),
          phone: new FormControl(phoneDefault),
          sms_phone: new FormControl(smsPhoneDefault),
          communication_config: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig>({
            communication_group: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup>({
              quote_announcements: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({
                  enabled: new FormControl(enableAnnouncementsForMe ? emailEnabledDefault : false),
                }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({
                  enabled: new FormControl(enableAnnouncementsForMe ? phoneEnabledDefault : false),
                }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({
                  enabled: new FormControl(enableAnnouncementsForMe ? smsEnabledDefault : false),
                }),
              }),
              quote_next_renewal: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({ enabled: new FormControl(enableRenewalForMe ? emailEnabledDefault : false) }),
                phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({ enabled: new FormControl(enableRenewalForMe ? phoneEnabledDefault : false) }),
                sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: new FormControl(enableRenewalForMe ? smsEnabledDefault : false) }),
              }),
            }),
          }),
        });
        this.contactsArray.push(group);
        this._changeDetectorRef.detectChanges();
      });
  }

  public addListenerModal(): void {
    const preselected = (this.listenerContacts || [])
      .filter((g) => (g.get('user_id')?.value ?? null) !== null)
      .filter((g) => {
        const annEmail = Boolean(g.get(`communication_config.communication_group.quote_announcements.email.enabled`)?.value);
        const annPhone = Boolean(g.get(`communication_config.communication_group.quote_announcements.phone.enabled`)?.value);
        const renEmail = Boolean(g.get(`communication_config.communication_group.quote_next_renewal.email.enabled`)?.value);
        const renPhone = Boolean(g.get(`communication_config.communication_group.quote_next_renewal.phone.enabled`)?.value);
        return annEmail || annPhone || renEmail || renPhone;
      })
      .map((g) => g.get('user_id')?.value ?? null)
      .filter((id: number | null) => id !== null) as number[];

    const ref = this._dialog.open(IntakeFormContactsDialogComponent, {
      width: '90vw',
      maxWidth: '100vw',
      height: '90vh',
      maxHeight: '90vh',
      data: { preselectedPrincipalContactIds: preselected },
      disableClose: true,
      panelClass: 'cipx-intake-contacts-dialog',
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.selected || !Array.isArray(result.selected)) return;
      const selected = result.selected as Array<{
        user_id: number;
        user_first_name: string;
        user_last_name: string;
        user_email_address: string;
        user_phone: string;
      }>;
      // Compute customer defaults from current user's preferences
      const cu = this._currentUser?.current?.();
      const userInfo: any | null = (cu as any)?.user_info ?? null;
      const allPrefs: any | null = userInfo?.preferences ?? null;
      const notifications: any | null = allPrefs?.notifications ?? null;
      const notifyMeBy: any | null = notifications?.notify_me_by ?? null;
      const renewalFlags: any | null = notifications?.qr_renewal_compliance_notifications ?? null;
      const resolveEnabledDefaults = (targetSection: 'quote_announcements' | 'quote_next_renewal') => {
        const sectionAllowed =
          targetSection === 'quote_announcements'
            ? renewalFlags?.quotecustomer !== false
            : renewalFlags?.renewalcustomer !== false;
        const defaultEmailEnabled = Boolean(notifyMeBy?.notify_me_email) && sectionAllowed;
        const defaultPhoneEnabled = Boolean(notifyMeBy?.notify_me_phone) && sectionAllowed;
        if (defaultEmailEnabled || defaultPhoneEnabled) {
          return { emailEnabled: defaultEmailEnabled, phoneEnabled: defaultPhoneEnabled };
        }
        // If user explicitly selects contacts, ensure at least one method is enabled.
        return { emailEnabled: true, phoneEnabled: false };
      };
      const announcementDefaults = resolveEnabledDefaults('quote_announcements');
      const renewalDefaults = resolveEnabledDefaults('quote_next_renewal');

      // Reconcile listeners globally: enable/disable in BOTH sections
      const selectedIds = new Set<number>(selected.map((s) => s.user_id));
      for (let i = 0; i < this.contactsArray.length; i++) {
        const ctrl = this.contactsArray.at(i) as FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>;
        const type = ctrl.get('contact_type')?.value;
        if (type !== CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener) continue;
        const id = (ctrl.get('user_id')?.value as number | null) ?? null;
        if (id === null) continue; // owner-derived "listener"

        const annEmailEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_announcements.email.enabled`
        ) as FormControl;
        const annPhoneEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_announcements.phone.enabled`
        ) as FormControl;
        const annSmsEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_announcements.sms.enabled`
        ) as FormControl | null;
        const renEmailEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_next_renewal.email.enabled`
        ) as FormControl;
        const renPhoneEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_next_renewal.phone.enabled`
        ) as FormControl;
        const renSmsEnabledCtrl = ctrl.get(
          `communication_config.communication_group.quote_next_renewal.sms.enabled`
        ) as FormControl | null;

        if (!selectedIds.has(id)) {
          annEmailEnabledCtrl?.setValue(false);
          annPhoneEnabledCtrl?.setValue(false);
          annSmsEnabledCtrl?.setValue(false);
          renEmailEnabledCtrl?.setValue(false);
          renPhoneEnabledCtrl?.setValue(false);
          renSmsEnabledCtrl?.setValue(false);
        } else {
          annEmailEnabledCtrl?.setValue(announcementDefaults.emailEnabled);
          annPhoneEnabledCtrl?.setValue(announcementDefaults.phoneEnabled);
          annSmsEnabledCtrl?.setValue(false);
          renEmailEnabledCtrl?.setValue(renewalDefaults.emailEnabled);
          renPhoneEnabledCtrl?.setValue(renewalDefaults.phoneEnabled);
          renSmsEnabledCtrl?.setValue(false);
        }
      }

      // Append newly selected listeners; avoid duplicates by user_id
      const existingIds = new Set(
        (this.listenerContacts || [])
          .map((g) => g.get('user_id')?.value)
          .filter((id: number | null) => id !== null) as number[]
      );
      selected
        .filter((c) => !existingIds.has(c.user_id))
        .forEach((c) => {
          const group = new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsContact>({
            contact_type: new FormControl<CIPXQuoteRequestIntakeFormCommunicationsContactTypeType>(
              CIPXQuoteRequestIntakeFormCommunicationsContactType.Listener,
              { nonNullable: true }
            ),
            first_name: new FormControl(c.user_first_name || ''),
            last_name: new FormControl(c.user_last_name || ''),
            user_id: new FormControl<number | null>(c.user_id ?? null),
            email: new FormControl(c.user_email_address || '', [Validators.email]),
            phone: new FormControl(c.user_phone || ''),
            sms_phone: new FormControl(c.user_phone || ''),
            communication_config: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationConfig>({
              communication_group: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationGroup>({
                quote_announcements: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                  email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({
                    enabled: new FormControl(announcementDefaults.emailEnabled),
                  }),
                  phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({
                    enabled: new FormControl(announcementDefaults.phoneEnabled),
                  }),
                  sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: new FormControl(false) }),
                }),
                quote_next_renewal: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsCommunicationMethods>({
                  email: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsEmailMethod>({
                    enabled: new FormControl(renewalDefaults.emailEnabled),
                  }),
                  phone: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsPhoneMethod>({
                    enabled: new FormControl(renewalDefaults.phoneEnabled),
                  }),
                  // Only account user can receive SMS; keep disabled here
                  sms: new FormGroup<CIPXQuoteRequestIntakeFormCommunicationsSmsMethod>({ enabled: new FormControl({ value: false, disabled: true }) }),
                }),
              }),
            }),
          });
          this.contactsArray.push(group);
        });

      this._changeDetectorRef.detectChanges();
      this._syncListenerContactAvailability();
    });
  }
}
